interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
  ttlMs: number;
}

interface CacheOptions {
  /** Time to live in milliseconds */
  ttlMs?: number;
  /** Cache version for invalidation */
  version?: string;
  /** Whether to use memory cache (default: true) */
  useMemory?: boolean;
  /** Whether to use localStorage cache (default: true) */
  useLocalStorage?: boolean;
}

type CacheFetcher<T> = () => Promise<T> | T;

export class CacheService {
  private static instance: CacheService;
  private memoryCache = new Map<string, CacheEntry<any>>();
  
  // Default configurations
  private readonly DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly DEFAULT_VERSION = '1.0.0';
  private readonly CACHE_KEY_PREFIX = 'aizen_cache_';

  private constructor() {}

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Get cached data or fetch if not available/expired
   */
  public async get<T>(
    key: string,
    fetcher: CacheFetcher<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    const opts = this.mergeOptions(options);
    
    // Try memory cache first (fastest)
    if (opts.useMemory) {
      const memoryData = this.getFromMemory<T>(key, opts);
      if (memoryData !== null) {
        return memoryData;
      }
    }

    // Try localStorage cache
    if (opts.useLocalStorage) {
      const localData = this.getFromLocalStorage<T>(key, opts);
      if (localData !== null) {
        // Update memory cache if enabled
        if (opts.useMemory) {
          this.setInMemory(key, localData, opts);
        }
        return localData;
      }
    }

    // Fetch fresh data
    const freshData = await fetcher();
    
    // Cache the fresh data
    this.set(key, freshData, options);
    
    return freshData;
  }

  /**
   * Set data in cache
   */
  public set<T>(key: string, data: T, options: CacheOptions = {}): void {
    const opts = this.mergeOptions(options);
    
    if (opts.useMemory) {
      this.setInMemory(key, data, opts);
    }
    
    if (opts.useLocalStorage) {
      this.setInLocalStorage(key, data, opts);
    }
  }

  /**
   * Get data directly from cache (without fetcher fallback)
   */
  public getSync<T>(key: string, options: CacheOptions = {}): T | null {
    const opts = this.mergeOptions(options);
    
    // Try memory first
    if (opts.useMemory) {
      const memoryData = this.getFromMemory<T>(key, opts);
      if (memoryData !== null) {
        return memoryData;
      }
    }

    // Try localStorage
    if (opts.useLocalStorage) {
      return this.getFromLocalStorage<T>(key, opts);
    }

    return null;
  }

  /**
   * Clear specific cache entry
   */
  public clear(key: string): void {
    // Clear from memory
    this.memoryCache.delete(key);
    
    // Clear from localStorage
    try {
      localStorage.removeItem(this.CACHE_KEY_PREFIX + key);
    } catch (error) {
      console.warn(`Error clearing cache for key ${key}:`, error);
    }
  }

  /**
   * Clear all cache entries
   */
  public clearAll(): void {
    // Clear memory cache
    this.memoryCache.clear();
    
    // Clear localStorage cache entries
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Error clearing localStorage cache:', error);
    }
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  public getStats(): { memoryEntries: number; localStorageEntries: number } {
    let localStorageEntries = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          localStorageEntries++;
        }
      }
    } catch (error) {
      // localStorage unavailable
    }

    return {
      memoryEntries: this.memoryCache.size,
      localStorageEntries
    };
  }

  // Private helper methods

  private mergeOptions(options: CacheOptions): Required<CacheOptions> {
    return {
      ttlMs: options.ttlMs ?? this.DEFAULT_TTL_MS,
      version: options.version ?? this.DEFAULT_VERSION,
      useMemory: options.useMemory ?? true,
      useLocalStorage: options.useLocalStorage ?? true
    };
  }

  private getFromMemory<T>(key: string, options: Required<CacheOptions>): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;

    // Check version
    if (entry.version !== options.version) {
      this.memoryCache.delete(key);
      return null;
    }

    // Check expiration
    const now = Date.now();
    if (now - entry.timestamp > entry.ttlMs) {
      this.memoryCache.delete(key);
      return null;
    }

    return entry.data;
  }

  private setInMemory<T>(key: string, data: T, options: Required<CacheOptions>): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      version: options.version,
      ttlMs: options.ttlMs
    };
    
    this.memoryCache.set(key, entry);
  }

  private getFromLocalStorage<T>(key: string, options: Required<CacheOptions>): T | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY_PREFIX + key);
      if (!cached) return null;

      const entry: CacheEntry<T> = JSON.parse(cached);
      
      // Check version
      if (entry.version !== options.version) {
        localStorage.removeItem(this.CACHE_KEY_PREFIX + key);
        return null;
      }

      // Check expiration
      const now = Date.now();
      if (now - entry.timestamp > entry.ttlMs) {
        localStorage.removeItem(this.CACHE_KEY_PREFIX + key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn(`Error reading from localStorage cache for key ${key}:`, error);
      return null;
    }
  }

  private setInLocalStorage<T>(key: string, data: T, options: Required<CacheOptions>): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: options.version,
        ttlMs: options.ttlMs
      };
      
      localStorage.setItem(this.CACHE_KEY_PREFIX + key, JSON.stringify(entry));
    } catch (error) {
      console.warn(`Error saving to localStorage cache for key ${key}:`, error);
      // Graceful degradation - continue without localStorage caching
    }
  }
}

// Export singleton instance
export const cacheService = CacheService.getInstance(); 